import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  fetchAllRegistrations, 
  updateRegistrationStatus, 
  checkInAttendee, 
  deleteRegistration,
  selectAllRegistrations, 
  selectRegistrationLoading, 
  selectRegistrationError,
  selectRegistrationSuccess,
  clearRegistrationSuccess,
  clearRegistrationError
} from '../../data/registrationSlice';
import { fetchEvents, selectAllEvents } from '../../data/eventSlice';

const AllEventRegistrations = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const allRegistrations = useSelector(selectAllRegistrations);
  const events = useSelector(selectAllEvents);
  const loading = useSelector(selectRegistrationLoading);
  const error = useSelector(selectRegistrationError);
  const success = useSelector(selectRegistrationSuccess);
  
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEvent, setFilterEvent] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('registration_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  useEffect(() => {
    // Fetch events first
    dispatch(fetchEvents());
  }, [dispatch]);
  
  useEffect(() => {
    // Once we have events, fetch registrations for all events
    if (events && events.length > 0) {
      dispatch(fetchAllRegistrations(events));
    }
  }, [events, dispatch]);
  
  useEffect(() => {
    // Clear success/error messages when component unmounts
    return () => {
      dispatch(clearRegistrationSuccess());
      dispatch(clearRegistrationError());
    };
  }, [dispatch]);
  
  useEffect(() => {
    if (success) {
      // Close modals if action was successful
      setShowStatusModal(false);
      setSelectedRegistration(null);
      
      // Clear success message after 5 seconds
      const timer = setTimeout(() => {
        dispatch(clearRegistrationSuccess());
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);
  
  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  const handleCheckIn = (registrationId) => {
    dispatch(checkInAttendee(registrationId));
  };
  
  const openStatusModal = (registration) => {
    setSelectedRegistration(registration);
    setSelectedStatus(registration.status || 'registered');
    setShowStatusModal(true);
  };
  
  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedRegistration(null);
  };
  
  const handleStatusChange = (e) => {
    e.preventDefault();
    if (selectedRegistration) {
      dispatch(updateRegistrationStatus({
        registrationId: selectedRegistration.id,
        status: selectedStatus
      }));
    }
  };
  
  const handleDeleteRegistration = (registrationId) => {
    if (window.confirm(t('confirm_delete_registration'))) {
      dispatch(deleteRegistration(registrationId));
    }
  };
  
  // Filtering and sorting logic
  const filteredRegistrations = allRegistrations
    .filter(reg => {
      // Filter by status
      if (filterStatus !== 'all' && reg.status !== filterStatus) {
        return false;
      }
      
      // Filter by event
      if (filterEvent !== 'all' && reg.event_id !== parseInt(filterEvent)) {
        return false;
      }
      
      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          reg.name?.toLowerCase().includes(term) ||
          reg.email?.toLowerCase().includes(term) ||
          reg.phone?.toLowerCase().includes(term) ||
          reg.notes?.toLowerCase().includes(term) ||
          reg.title?.toLowerCase().includes(term) ||
          reg.location?.toLowerCase().includes(term)
        );
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort by the selected field
      const fieldA = a[sortField] || '';
      const fieldB = b[sortField] || '';
      
      if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === 'asc' 
          ? fieldA.localeCompare(fieldB)
          : fieldB.localeCompare(fieldA);
      } else {
        return sortDirection === 'asc' 
          ? fieldA - fieldB
          : fieldB - fieldA;
      }
    });
  
  // Count stats
  const registeredCount = allRegistrations.filter(r => r.status === 'registered').length;
  const attendedCount = allRegistrations.filter(r => r.status === 'attended').length;
  const totalEvents = events?.length || 0; // Use events length instead of distinct event_ids
  
  // Handle sorting toggle
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('all_event_registrations')}</h1>
          <p className="text-gray-600">{t('manage_all_registrations')}</p>
        </div>
        
        {/* Export button */}
        <button
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          {t('export_to_csv')}
        </button>
      </div>
      
      {/* Registration Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white shadow rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-gray-900">{allRegistrations.length}</div>
              <div className="text-sm font-medium text-gray-500">{t('total_registrations')}</div>
            </div>
            <div className="bg-indigo-100 p-3 rounded-full">
              <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-gray-900">{registeredCount}</div>
              <div className="text-sm font-medium text-gray-500">{t('registered')}</div>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-gray-900">{attendedCount}</div>
              <div className="text-sm font-medium text-gray-500">{t('attended')}</div>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-3xl font-bold text-gray-900">{totalEvents}</div>
              <div className="text-sm font-medium text-gray-500">Events</div>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{typeof error === 'object' ? 'An error occurred. Please try again.' : error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Success message */}
      {success && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Status filter */}
          <div>
            <label htmlFor="filterStatus" className="block text-sm font-medium text-gray-700 mb-1">
              {t('filter_by_status')}
            </label>
            <select
              id="filterStatus"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="all">{t('all_statuses')}</option>
              <option value="registered">{t('registered')}</option>
              <option value="attended">{t('attended')}</option>
              <option value="cancelled">{t('cancelled')}</option>
              <option value="no_show">{t('no_show')}</option>
            </select>
          </div>
          
          {/* Event filter */}
          <div>
            <label htmlFor="filterEvent" className="block text-sm font-medium text-gray-700 mb-1">
              {t('filter_by_event')}
            </label>
            <select
              id="filterEvent"
              value={filterEvent}
              onChange={(e) => setFilterEvent(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="all">{t('all_events')}</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>{event.title}</option>
              ))}
            </select>
          </div>
          
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              {t('search')}
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                name="search"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                placeholder={t('search_registrations')}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Registrations Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">{t('registrations_list')}</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {filteredRegistrations.length} {t('results_found')}
          </p>
        </div>
        
        {loading && allRegistrations.length === 0 ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {filteredRegistrations.length === 0 ? (
              <div className="text-center py-10">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('no_registrations_found')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('try_adjusting_filters')}</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('attendee')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => toggleSort('title')}
                    >
                      <div className="flex items-center">
                        {t('event')}
                        {sortField === 'title' && (
                          <svg className={`ml-1 w-4 h-4 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => toggleSort('registration_date')}
                    >
                      <div className="flex items-center">
                        {t('registration_date')}
                        {sortField === 'registration_date' && (
                          <svg className={`ml-1 w-4 h-4 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('status')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('notes_feedback')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRegistrations.map((registration) => (
                    <tr key={registration.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <svg className="h-6 w-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{registration.name}</div>
                            <div className="text-sm text-gray-500">{registration.email}</div>
                            {registration.phone && <div className="text-sm text-gray-500">{registration.phone}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link 
                          to={`/admin/events/${registration.event_id}/registrations`}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                        >
                          {registration.title}
                        </Link>
                        <div className="text-xs text-gray-500">{registration.location}</div>
                        <div className="text-xs text-gray-500">{formatDate(registration.date)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(registration.registration_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${registration.status === 'registered' ? 'bg-blue-100 text-blue-800' : 
                          registration.status === 'attended' ? 'bg-green-100 text-green-800' : 
                          registration.status === 'cancelled' ? 'bg-red-100 text-red-800' : 
                          registration.status === 'no_show' ? 'bg-gray-100 text-gray-800' :
                          'bg-gray-100 text-gray-800'}`}
                        >
                          {registration.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {registration.notes && (
                          <div className="mb-2">
                            <p className="text-xs font-medium text-gray-500">Notes:</p>
                            <p className="text-sm text-gray-700 line-clamp-2">{registration.notes}</p>
                          </div>
                        )}
                        
                        {registration.feedback && (
                          <div>
                            <p className="text-xs font-medium text-gray-500">Feedback:</p>
                            <div className="flex mb-1">
                              {[...Array(5)].map((_, i) => (
                                <svg 
                                  key={i}
                                  className={`h-3 w-3 ${i < registration.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                                  xmlns="http://www.w3.org/2000/svg" 
                                  viewBox="0 0 20 20" 
                                  fill="currentColor"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                            <p className="text-sm text-gray-700 line-clamp-2">{registration.feedback}</p>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {registration.status === 'registered' && (
                            <button
                              onClick={() => handleCheckIn(registration.id)}
                              className="text-green-600 hover:text-green-900"
                              title={t('check_in')}
                            >
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => openStatusModal(registration)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title={t('update_status')}
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteRegistration(registration.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('delete')}
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      
      {/* Status Update Modal */}
      {showStatusModal && selectedRegistration && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div 
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog" 
              aria-modal="true" 
              aria-labelledby="modal-headline"
            >
              <form onSubmit={handleStatusChange}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-headline">
                    {t('update_registration_status')}
                  </h3>
                  
                  <div className="mb-4">
                    <label htmlFor="attendee" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('attendee')}
                    </label>
                    <p className="text-base text-gray-900">{selectedRegistration.name} ({selectedRegistration.email})</p>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="event" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('event')}
                    </label>
                    <p className="text-base text-gray-900">{selectedRegistration.title}</p>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('status')}
                    </label>
                    <select
                      id="status"
                      name="status"
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <option value="registered">{t('registered')}</option>
                      <option value="attended">{t('attended')}</option>
                      <option value="cancelled">{t('cancelled')}</option>
                      <option value="no_show">{t('no_show')}</option>
                    </select>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('updating')}
                      </>
                    ) : t('update_status')}
                  </button>
                  <button
                    type="button"
                    onClick={closeStatusModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllEventRegistrations;
